// OrderCardList — the order CARD list (redesign Task 5, amendment F4: cards,
// not a table). One card per order: a header row (Order placed / Total /
// Bill to / Ship to / the order number as a button-link / the status badge),
// up to three item thumbnails with names and quantities, a "+ N more items"
// line, and the View details / Buy again actions. `compact` (the Overview
// landing) shows the header row and the first item only, with no actions.
//
// D5 (ship before the API-4 deploy): every block renders only when its field
// is present — placed_at, bill_to, ship_to and items_preview are all optional
// on OrderSummary, so an embed pointed at a pre-API-4 server still renders
// number, total and status.
//
// Addresses come from the order's own frozen snapshot (bill_to/ship_to,
// migration 124) — NEVER listAddresses() (ruling F5: the saved-address list is
// mutable and the order must show what was on it at placement).
//
// The order number is a real <button type="button"> styled as a link (rule 73
// at birth) named "Order <number>" — the retired card row was a
// click+keydown div with role="button", invisible to screen readers as a
// named control and keyboard-dead without its own key handler.
//
// Inline styles only — no Tailwind. Themed via useEmbedTokens(); the status
// badge reads the one status map (embed-shared/status.ts) via
// EmbedStatusBadge, so list and detail can never paint the same enum twice.

import { useEmbedTokens, EmbedCard, EmbedButton, EmbedStatusBadge } from "../../embed-shared/primitives";
import { formatCurrency } from "../../embed-shared/currency";
import { formatInstantDate } from "../../embed-shared/format";
import { ItemThumb } from "./ItemThumb";
import type { OrderSummary } from "../../embed-shared/types";

export interface OrderCardListProps {
  orders: OrderSummary[];
  /** Opens the order inside the shell (the detail lives in the same section). */
  onOpenOrder: (orderId: string) => void;
  /** Re-order helper; absent = the action is not offered. */
  onBuyAgain?: (order: OrderSummary) => void;
  /** Overview's compact variant (redesign Task 6): header row + first item
   *  only, no actions. Row COUNT is the caller's decision — this component
   *  has no count opinion; the Overview caller slices its list to at most 5
   *  (OverviewPanel RECENT_ORDER_LIMIT) before handing it here. */
  compact?: boolean;
}

const PREVIEW_LIMIT = 3;

/** One city/state line — the card header shows a one-line locator, never the full street. */
function cityLine(addr: { city: string; state: string }): string {
  return `${addr.city}, ${addr.state}`;
}

function HeaderField({ label, children }: { label: string; children: React.ReactNode }) {
  const t = useEmbedTokens();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>{label}</span>
      {children}
    </div>
  );
}

export function OrderCardList({ orders, onOpenOrder, onBuyAgain, compact = false }: OrderCardListProps) {
  const t = useEmbedTokens();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
      {orders.map((order) => {
        const previews = compact ? (order.items_preview ?? []).slice(0, 1) : (order.items_preview ?? []).slice(0, PREVIEW_LIMIT);
        const moreCount = order.line_count !== undefined ? order.line_count - previews.length : 0;
        return (
          <EmbedCard key={order.id} padding={t.spacing.md}>
            <div data-testid={`order-card-${order.id}`} style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
              {/* Header row: the facts, each its own labelled field. flexWrap
                  keeps the row readable at phone width without any horizontal
                  scroll (minWidth: 0 on each field lets long values shrink). */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: `${t.spacing.sm}px ${t.spacing.lg}px`, alignItems: "flex-start" }}>
                {order.placed_at && (
                  <HeaderField label="Order placed">
                    <span style={{ fontSize: t.fontSize.sm, fontWeight: t.fontWeight.medium, color: t.colors.text }}>
                      {formatInstantDate(order.placed_at, { withYear: false })}
                    </span>
                  </HeaderField>
                )}
                <HeaderField label="Total">
                  <span style={{ fontSize: t.fontSize.sm, fontWeight: t.fontWeight.medium, color: t.colors.text, fontVariantNumeric: "tabular-nums" }}>
                    {formatCurrency(order.total)}
                  </span>
                </HeaderField>
                {order.bill_to && (
                  <HeaderField label="Bill to">
                    <span style={{ fontSize: t.fontSize.sm, color: t.colors.text, overflowWrap: "anywhere" }}>
                      {cityLine(order.bill_to)}
                    </span>
                  </HeaderField>
                )}
                {order.ship_to && (
                  <HeaderField label="Ship to">
                    <span style={{ fontSize: t.fontSize.sm, color: t.colors.text, overflowWrap: "anywhere" }}>
                      {cityLine(order.ship_to)}
                    </span>
                  </HeaderField>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: t.spacing.sm, marginLeft: "auto" }}>
                  <button
                    type="button"
                    onClick={() => onOpenOrder(order.id)}
                    style={{
                      fontFamily: t.fontFamilyMono,
                      fontSize: t.fontSize.sm,
                      fontWeight: t.fontWeight.medium,
                      color: t.colors.link,
                      background: "transparent",
                      border: 0,
                      padding: 0,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      // Task 12 check (c): a real target, 24px on a mouse / 44px on touch.
                      display: "inline-flex",
                      alignItems: "center",
                      minHeight: t.pointer === "fine" ? 24 : t.size.touchTarget,
                    }}
                  >
                    Order {order.order_number}
                  </button>
                  <EmbedStatusBadge domain="order" value={order.status} />
                </div>
              </div>

              {/* Body: up to three preview rows (compact: the first only). */}
              {previews.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
                  {/* Keyed by line POSITION, not product_id: items_preview is
                      per-LINE, and under the per-line store-association
                      rulings one product can span two lines (each line names
                      its own store) — both can sit in the top 3. The wire
                      carries no line id (rule 9: database-internal columns
                      never reach the API), so position is the stablest key
                      the previews array itself admits. */}
                  {previews.map((item, index) => (
                    <div key={`${item.product_id}-${index}`} style={{ display: "flex", alignItems: "center", gap: t.spacing.md, minWidth: 0 }}>
                      <ItemThumb name={item.product_name} imageUrl={item.image_url} />
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                        <span style={{ fontSize: t.fontSize.sm, fontWeight: t.fontWeight.medium, color: t.colors.text, overflowWrap: "anywhere" }}>
                          {item.product_name}
                        </span>
                        <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>Qty {item.quantity}</span>
                      </div>
                    </div>
                  ))}
                  {!compact && moreCount > 0 && (
                    <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>+ {moreCount} more items</span>
                  )}
                </div>
              )}

              {/* Actions — absent entirely in compact. */}
              {!compact && (
                <div style={{ display: "flex", gap: t.spacing.sm, flexWrap: "wrap" }}>
                  <EmbedButton type="button" variant="secondary" size="sm" onClick={() => onOpenOrder(order.id)}>
                    View order details
                  </EmbedButton>
                  {onBuyAgain && (
                    <EmbedButton type="button" variant="secondary" size="sm" onClick={() => onBuyAgain(order)}>
                      Buy again
                    </EmbedButton>
                  )}
                </div>
              )}
            </div>
          </EmbedCard>
        );
      })}
    </div>
  );
}
